import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RefreshCw, Link2Off, Eye, AlertCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/EmptyState";
import { Database } from "lucide-react";

type DataSource = {
  id: string;
  name: string;
  source_type: string;
  status: string | null;
  user_id: string;
  config: any;
  created_at: string;
  updated_at: string;
};

const getStatusColor = (status: string | null) => {
  switch (status) {
    case "Connected":
      return "bg-green-500/10 text-green-500 hover:bg-green-500/20";
    case "Error":
      return "bg-red-500/10 text-red-500 hover:bg-red-500/20";
    case "Syncing":
      return "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20";
  }
};

const crmTypeOf = (sourceType: string): "hubspot" | "zoho" | null => {
  const t = (sourceType || "").toLowerCase();
  if (t.includes("hubspot")) return "hubspot";
  if (t.includes("zoho")) return "zoho";
  return null;
};

/**
 * Real connected sources only. There is no sample/mock fallback: if the user
 * has nothing connected we say so. "Sync" performs an actual fetch against the
 * provider through the crm-data edge function and records the true row count.
 */
export const DataSources = () => {
  const queryClient = useQueryClient();
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<DataSource | null>(null);
  const [preview, setPreview] = useState<{ rows: any[]; loading: boolean; error?: string }>({
    rows: [],
    loading: false,
  });

  const { data: session, isLoading: isAuthLoading } = useQuery({
    queryKey: ["auth-session"],
    queryFn: async () => (await supabase.auth.getSession()).data.session,
  });

  const {
    data: sources = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["data-sources", session?.user?.id],
    enabled: Boolean(session?.user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("data_sources")
        .select("*")
        .eq("user_id", session!.user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DataSource[];
    },
  });

  const setStatus = async (source: DataSource, status: string, config?: any) => {
    await supabase
      .from("data_sources")
      .update({
        status,
        updated_at: new Date().toISOString(),
        ...(config ? { config } : {}),
      })
      .eq("id", source.id)
      .eq("user_id", session!.user.id);
    queryClient.invalidateQueries({ queryKey: ["data-sources"] });
  };

  const handleSync = async (source: DataSource) => {
    if (!session?.user) return;
    const crm = crmTypeOf(source.source_type);
    setSyncingId(source.id);
    try {
      if (crm) {
        await setStatus(source, "Syncing");
        const { data, error } = await supabase.functions.invoke("crm-data", {
          body: { crm_type: crm, action: "list_contacts" },
        });
        if (error || !data?.success) {
          const message = data?.error || error?.message || "Sync failed";
          await setStatus(source, "Error", {
            ...(source.config || {}),
            last_error: message,
            last_synced_at: new Date().toISOString(),
          });
          toast.error(message);
          return;
        }
        const rows = data.data ?? [];
        await setStatus(source, "Connected", {
          ...(source.config || {}),
          record_count: rows.length,
          last_error: null,
          last_synced_at: new Date().toISOString(),
          sample: rows.slice(0, 20),
        });
        toast.success(`Synced ${rows.length} records from ${source.name}`);
      } else {
        // File-based source: recount what is actually stored for it.
        const { count } = await supabase
          .from("data_imports")
          .select("id", { count: "exact", head: true })
          .eq("user_id", session.user.id)
          .eq("file_name", source.name);
        await setStatus(source, "Connected", {
          ...(source.config || {}),
          import_count: count ?? 0,
          last_synced_at: new Date().toISOString(),
        });
        toast.success(
          count
            ? `${source.name}: ${count} import${count === 1 ? "" : "s"} on record`
            : `${source.name} has no imports yet — upload a file to refresh it.`,
        );
      }
    } catch (e: any) {
      toast.error(e?.message || "Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  const handleView = async (source: DataSource) => {
    setViewing(source);
    const cached = (source.config || {}).sample;
    if (Array.isArray(cached) && cached.length) {
      setPreview({ rows: cached, loading: false });
      return;
    }
    const crm = crmTypeOf(source.source_type);
    if (!crm) {
      setPreview({ rows: [], loading: false });
      return;
    }
    setPreview({ rows: [], loading: true });
    const { data, error } = await supabase.functions.invoke("crm-data", {
      body: { crm_type: crm, action: "list_contacts" },
    });
    if (error || !data?.success) {
      setPreview({
        rows: [],
        loading: false,
        error: data?.error || error?.message || "Could not load records",
      });
      return;
    }
    setPreview({ rows: (data.data ?? []).slice(0, 20), loading: false });
  };

  const handleDisconnect = async (source: DataSource) => {
    if (!session?.user) return;
    const { error } = await supabase
      .from("data_sources")
      .delete()
      .eq("id", source.id)
      .eq("user_id", session.user.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Data source disconnected");
    refetch();
  };

  const formatLastSync = (timestamp: string | null) => {
    if (!timestamp) return "Never";
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  if (isAuthLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Connected data sources</h2>
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (!session) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Connected data sources</h2>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Sign in required</AlertTitle>
          <AlertDescription>Sign in to see the sources you've connected.</AlertDescription>
        </Alert>
      </Card>
    );
  }

  const previewColumns: string[] = preview.rows.length
    ? Array.from(
        preview.rows.reduce<Set<string>>((set, r: any) => {
          Object.keys(r ?? {}).forEach((k) => set.add(k));
          return set;
        }, new Set<string>()),
      ).slice(0, 6)
    : [];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Connected data sources</h2>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" /> Reload
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load your sources</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : sources.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No data sources connected yet"
          description="Connect HubSpot, Zoho, Google Sheets or Dropbox above, or upload a file, and it will appear here."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Records</TableHead>
              <TableHead>Last sync</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="font-medium">{source.name}</TableCell>
                <TableCell>{source.source_type}</TableCell>
                <TableCell>
                  {(source.config || {}).record_count ??
                    (source.config || {}).import_count ??
                    "—"}
                </TableCell>
                <TableCell>
                  {formatLastSync((source.config || {}).last_synced_at ?? source.updated_at)}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className={getStatusColor(source.status)}>
                    {source.status || "Unknown"}
                  </Badge>
                  {(source.config || {}).last_error && (
                    <p className="text-xs text-destructive mt-1 max-w-[220px]">
                      {(source.config as any).last_error}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Sync"
                      onClick={() => handleSync(source)}
                      disabled={syncingId === source.id}
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${syncingId === source.id ? "animate-spin" : ""}`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View records"
                      onClick={() => handleView(source)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Disconnect"
                      onClick={() => handleDisconnect(source)}
                    >
                      <Link2Off className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(viewing)} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewing?.name}</DialogTitle>
            <DialogDescription>
              {viewing?.source_type} · live records from this source
            </DialogDescription>
          </DialogHeader>
          {preview.loading ? (
            <div className="flex items-center gap-2 p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading records…
            </div>
          ) : preview.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{preview.error}</AlertDescription>
            </Alert>
          ) : preview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground p-2">
              No records available yet. Run a sync to pull the latest data.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-[50vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    {previewColumns.map((c) => (
                      <TableHead key={c}>{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r, i) => (
                    <TableRow key={i}>
                      {previewColumns.map((c) => (
                        <TableCell key={c} className="max-w-[200px] truncate">
                          {r[c] === null || r[c] === undefined ? "—" : String(r[c])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
